import React from 'react';
import { useSelectedFinancialYear, isDateInYear } from 'hooks/useSelectedFinancialYear';

// ============================================================================
// FyDateInput — the posting date of a transaction document. The calendar offers
// only dates inside the financial year selected in the header (min / max); a
// date typed outside it, or left over after the header year changed, is flagged
// under the field at once instead of on save. A closed year is flagged too.
//
// Use it for the date that POSTS the document (invoice, note, booking, payment,
// receipt, journal, set-off, expense dates) — not for a supplier's own document
// date, due dates, cheque dates or report filters.
// ============================================================================

const fmt = (ymd: string) => new Date(`${ymd}T00:00:00`).toLocaleDateString();

export interface FyDateInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'min' | 'max'> {
  value: string;
  onChange: (value: string) => void;
  /** Hide the closed-year note (e.g. when viewing a posted document). Defaults to the field being disabled. */
  quiet?: boolean;
}

export const FyDateInput: React.FC<FyDateInputProps> = ({ value, onChange, quiet, disabled, className, style, ...rest }) => {
  const fy = useSelectedFinancialYear();
  const date = (value ?? '').slice(0, 10);
  const outside = !!date && !isDateInYear(date, fy);
  const silent = quiet ?? disabled;
  const message = !fy || silent ? null
    : fy.isClosed ? `Financial year ${fy.name} is closed — documents cannot be posted to it.`
    : outside ? `Outside the selected financial year (${fmt(fy.startDate)} – ${fmt(fy.endDate)}).`
    : null;

  return (
    <>
      <input
        {...rest}
        type="date"
        value={date}
        min={fy?.startDate}
        max={fy?.endDate}
        disabled={disabled}
        className={`${className ?? 'form-input'}${message ? ' form-field-error' : ''}`}
        style={style}
        title={fy ? `Financial year ${fy.name}: ${fmt(fy.startDate)} – ${fmt(fy.endDate)}` : undefined}
        onChange={e => onChange(e.target.value)}
      />
      {message && (
        <div className="form-error" style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '0.2rem' }}>{message}</div>
      )}
    </>
  );
};

export default FyDateInput;
